import { lookupNutrients } from "../constants/nutrients.js";
import { getWeeksInMonth, getISOWeek, getTodayDate } from "./formatting.js";

// ── Calorie / deficit calculations ────────────────────────────────────────────

/** Mifflin-St Jeor BMR (male). Returns null if inputs missing. */
export function calcBMR(weight, height, age) {
  const w = Number(weight);
  const h = Number(height);
  const a = Number(age);
  if (!w || !h || !a) return null;
  return Math.round(10 * w + 6.25 * h - 5 * a + 5);
}

/** Sedentary maintenance — BMR × 1.2, fixed regardless of workouts */
export function calcMaintenance(weight, height, age) {
  const bmr = calcBMR(weight, height, age);
  if (!bmr) return null;
  return Math.round(bmr * 1.2);
}

// ── Body composition ──────────────────────────────────────────────────────────

/** Navy Method body fat % (male). Returns null if inputs are invalid. */
export function calcNavyBF(neck, waist, height) {
  const neckCm = Number(neck);
  const waistCm = Number(waist);
  const heightCm = Number(height);
  if (!neckCm || !waistCm || !heightCm || waistCm <= neckCm) return null;
  const neckIn = neckCm / 2.54;
  const waistIn = waistCm / 2.54;
  const heightIn = heightCm / 2.54;
  const bf = 495 / (1.0324 - 0.19077 * Math.log10(waistIn - neckIn) + 0.15456 * Math.log10(heightIn)) - 450;
  return Math.round(bf * 10) / 10;
}

export function calcFatMass(weight, bodyFatPercent) {
  const w = Number(weight);
  const bf = Number(bodyFatPercent);
  if (!w || !Number.isFinite(bf)) return null;
  return Math.max(0, w * (bf / 100));
}

export function calcLeanMass(weight, bodyFatPercent) {
  const w = Number(weight);
  const fatMass = calcFatMass(w, bodyFatPercent);
  if (!w || fatMass == null) return null;
  return Math.max(0, w - fatMass);
}

/** Boer formula lean body mass (kg). Returns null if inputs missing. */
export function calcLBM(weight, height) {
  const w = Number(weight);
  const h = Number(height);
  if (!w || !h) return null;
  return Math.max(0, (0.407 * w) + (0.267 * h) - 19.2);
}

function round1(value) {
  return Math.round(value * 10) / 10;
}

function finitePositive(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function sortedWeightValues(weightLog = []) {
  return [...weightLog]
    .map(entry => finitePositive(entry?.weight ?? entry?.weight_kg))
    .filter(Boolean);
}

function heaviestLoggedWeight(weightLog = []) {
  const weights = sortedWeightValues(weightLog);
  return weights.length ? Math.max(...weights) : null;
}

function latestLoggedWeight(weightLog = []) {
  const entries = [...weightLog]
    .map(entry => ({
      date: String(entry?.date ?? entry?.entry_date ?? ""),
      weight: finitePositive(entry?.weight ?? entry?.weight_kg),
    }))
    .filter(entry => entry.weight);
  if (!entries.length) return null;
  entries.sort((a, b) => a.date.localeCompare(b.date));
  return entries[entries.length - 1].weight;
}

export function calcBodyFatFromLeanMass(weight, leanMass) {
  const w = finitePositive(weight);
  const lean = finitePositive(leanMass);
  if (!w || !lean) return null;
  const bf = Math.max(0, Math.min(75, (1 - (lean / w)) * 100));
  return round1(bf);
}

export function getBodyCompositionBaseline(fitnessGoals = {}, weightLog = []) {
  const explicitAnchorWeight = finitePositive(fitnessGoals.bodyFatAnchorWeight);
  const explicitAnchorBodyFat = finitePositive(fitnessGoals.bodyFatAnchorPercent);
  const navyBodyFat = calcNavyBF(fitnessGoals.neck, fitnessGoals.waist, fitnessGoals.height);
  const legacyAnchorWeight = heaviestLoggedWeight(weightLog);
  const currentWeight = finitePositive(fitnessGoals.weightCurrent);
  const latestWeight = latestLoggedWeight(weightLog);
  const hasExplicitAnchor = explicitAnchorWeight != null && explicitAnchorBodyFat != null;
  const measuredBodyFat = hasExplicitAnchor
    ? explicitAnchorBodyFat
    : navyBodyFat ?? finitePositive(fitnessGoals.bodyFatCurrent);
  const anchorWeight = hasExplicitAnchor
    ? explicitAnchorWeight
    : navyBodyFat != null
      ? latestWeight ?? currentWeight ?? legacyAnchorWeight
      : legacyAnchorWeight ?? currentWeight;
  if (!anchorWeight) return null;

  let leanMass = null;
  let source = "boer";
  if (measuredBodyFat != null) {
    leanMass = calcLeanMass(anchorWeight, measuredBodyFat);
    source = navyBodyFat != null ? "navy" : "manual";
  }
  if (leanMass == null) {
    leanMass = calcLBM(anchorWeight, fitnessGoals.height);
  }
  if (leanMass == null) return null;

  return {
    anchorWeight: round1(anchorWeight),
    anchorBodyFat: measuredBodyFat != null ? round1(measuredBodyFat) : null,
    leanMass: round1(leanMass),
    source,
    isLegacyAnchor: !explicitAnchorWeight && legacyAnchorWeight != null,
  };
}

export function calcDynamicBodyFat(weight, fitnessGoals = {}, weightLog = []) {
  const w = finitePositive(weight);
  const currentWeight = finitePositive(fitnessGoals.weightCurrent);
  const latestWeight = latestLoggedWeight(weightLog);
  const currentNavyBodyFat = calcNavyBF(fitnessGoals.neck, fitnessGoals.waist, fitnessGoals.height);
  const isCurrentWeight = w && (
    (currentWeight && Math.abs(w - currentWeight) < 0.05) ||
    (latestWeight && Math.abs(w - latestWeight) < 0.05)
  );
  if (isCurrentWeight && currentNavyBodyFat != null) {
    return currentNavyBodyFat;
  }
  const baseline = getBodyCompositionBaseline(fitnessGoals, weightLog);
  if (!baseline) return null;
  return calcBodyFatFromLeanMass(weight, baseline.leanMass);
}

export function calcDynamicLeanMass(weight, fitnessGoals = {}, weightLog = []) {
  const bodyFat = calcDynamicBodyFat(weight, fitnessGoals, weightLog);
  const lean = calcLeanMass(weight, bodyFat);
  return lean != null ? round1(lean) : null;
}

export function getCurrentBodyComposition(fitnessGoals = {}, weightLog = []) {
  const weight = latestLoggedWeight(weightLog) ?? finitePositive(fitnessGoals.weightCurrent);
  const bodyFat = weight != null ? calcDynamicBodyFat(weight, fitnessGoals, weightLog) : null;
  const leanMass = weight != null ? calcDynamicLeanMass(weight, fitnessGoals, weightLog) : null;
  return {
    weight,
    bodyFat,
    leanMass,
    baseline: getBodyCompositionBaseline(fitnessGoals, weightLog),
  };
}

export function buildBodyCompositionAnchor(fitnessGoals = {}, weightLog = []) {
  const measuredBodyFat = calcNavyBF(fitnessGoals.neck, fitnessGoals.waist, fitnessGoals.height)
    ?? finitePositive(fitnessGoals.bodyFatCurrent);
  const currentWeight = latestLoggedWeight(weightLog)
    ?? finitePositive(fitnessGoals.weightCurrent)
    ?? heaviestLoggedWeight(weightLog);
  if (measuredBodyFat == null || !currentWeight) return fitnessGoals;
  return {
    ...fitnessGoals,
    bodyFatCurrent: round1(measuredBodyFat),
    bodyFatAnchorPercent: round1(measuredBodyFat),
    bodyFatAnchorWeight: round1(currentWeight),
    bodyFatAnchorUpdatedAt: getTodayDate(),
  };
}

// ── Nutrition ─────────────────────────────────────────────────────────────────

/** Convert an ingredient amount to grams. cupGrams = grams per 1 cup for this food (food-specific). */
export function toGrams(amount, unit, cupGrams = 240) {
  const val = parseFloat(amount) || 0;
  if (unit === "ml")   return val;
  if (unit === "cups") return val * cupGrams;
  return val;
}

export function cupGramsForIngredient(ing, nutrients = null) {
  const local = lookupNutrients(ing?.name);
  const cupGrams = Number(
    ing?._cupGrams ??
    nutrients?._cupGrams ??
    ing?._nutrients?._cupGrams ??
    local?._cupGrams
  );
  return Number.isFinite(cupGrams) && cupGrams > 0 ? cupGrams : 240;
}

export function ingredientGrams(ing, fallbackAmount = 0) {
  const n = ing?._nutrients || lookupNutrients(ing?.name);
  const cupGrams = cupGramsForIngredient(ing, n);
  const amount = parseFloat(ing?.amount);
  const value = Number.isFinite(amount) ? amount : fallbackAmount;
  return toGrams(value, ing?.unit, cupGrams);
}

/** Calculate combined macros for an array of ingredients */
export function calcMacros(ingredients = []) {
  let cal = 0, pro = 0, carb = 0, fat = 0, fib = 0, known = 0;
  for (const ing of ingredients) {
    const n = ing._nutrients || lookupNutrients(ing.name);
    const g = ingredientGrams(ing, 100);
    if (n) {
      const scale = n._serving !== undefined ? n._serving : 100;
      cal  += (n.calories * g) / scale;
      pro  += (n.protein  * g) / scale;
      carb += (n.carbs    * g) / scale;
      fat  += (n.fat      * g) / scale;
      fib  += ((n.fiber || 0) * g) / scale;
      known++;
    }
  }
  return {
    calories: Math.round(cal),
    protein:  Math.round(pro),
    carbs:    Math.round(carb),
    fat:      Math.round(fat),
    fiber:    Math.round(fib * 10) / 10,
    known,
    total: ingredients.length,
  };
}

// ── Grocery projection ────────────────────────────────────────────────────────

/**
 * Projects grocery usage against the actual weeks the meal plan runs this month.
 * - mealPlans: { id: { id, name, days } }
 * - weekAssignments: { "2026-W10": planId }
 * - groceries: [{ name, amount, dailyUse?, ... }]
 */
export function projectMonthlyUsage(groceries, mealPlans, weekAssignments) {
  if (!groceries?.length) return [];

  const { DAYS, MEALS } = require("../constants/config");

  const now          = new Date();
  const daysInMonth  = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const monthWeeks   = getWeeksInMonth(now.getFullYear(), now.getMonth());

  // Sum plan ingredient usage across weeks that have an assignment this month
  const planUsage     = {}; // { normalizedName: totalGrams }
  const planItemWeeks = {}; // { normalizedName: Set of weekKeys where item appears }
  let assignedWeekCount = 0;

  for (const week of monthWeeks) {
    const planId = weekAssignments?.[week.key];
    if (!planId || !mealPlans?.[planId]) continue;
    assignedWeekCount++;
    const plan = mealPlans[planId];
    const keysThisWeek = new Set();
    for (const day of DAYS) {
      for (const meal of MEALS) {
        const m = plan.days?.[day]?.[meal];
        if (!m?.ingredients) continue;
        for (const ing of m.ingredients) {
          const key = (ing.name || "").toLowerCase().trim();
          if (!key) continue;
          planUsage[key] = (planUsage[key] || 0) + ingredientGrams(ing);
          keysThisWeek.add(key);
        }
      }
    }
    for (const key of keysThisWeek) {
      if (!planItemWeeks[key]) planItemWeeks[key] = new Set();
      planItemWeeks[key].add(week.key);
    }
  }

  // Fallback: if no week assignments, use the most recent plan × weeks in month
  const hasFallback = assignedWeekCount === 0 && mealPlans && Object.keys(mealPlans).length > 0;
  if (hasFallback) {
    const fallbackPlan = Object.values(mealPlans).slice(-1)[0];
    assignedWeekCount = 4;
    for (const day of DAYS) {
      for (const meal of MEALS) {
        const m = fallbackPlan?.days?.[day]?.[meal];
        if (!m?.ingredients) continue;
        for (const ing of m.ingredients) {
          const key = (ing.name || "").toLowerCase().trim();
          if (!key) continue;
          // accumulate one week's worth, multiply below
          planUsage[key] = (planUsage[key] || 0) + ingredientGrams(ing);
        }
      }
    }
    // multiply single-week totals by the fallback week count
    for (const key of Object.keys(planUsage)) {
      planUsage[key] *= assignedWeekCount;
    }
  }

  return groceries.map(g => {
    const key       = (g.name || "").toLowerCase().trim();
    const purchased = parseFloat(g.amount)    || 0;
    const dailyUse  = parseFloat(g.dailyUse)  || 0;
    // cookRatio: 1g dry = cookRatio g cooked. Plan amounts are cooked → divide to get dry needed.
    const cookRatio = parseFloat(g.cookRatio) || 1;

    // Match this grocery item against plan ingredient names
    let planNeededCooked = 0;
    let itemWeekCount = 0;
    for (const [uKey, amt] of Object.entries(planUsage)) {
      if (key === uKey || key.includes(uKey) || uKey.includes(key)) {
        planNeededCooked += amt;
        itemWeekCount = planItemWeeks[uKey]?.size || 0;
        break;
      }
    }
    const planNeeded = Math.round(planNeededCooked / cookRatio);

    // Use max(planNeeded, dailyUse×month) — daily use is a total-consumption estimate,
    // not an additive extra. Avoids double-counting items already in the plan.
    const dailyNeeded  = dailyUse * daysInMonth;
    const totalNeeded  = Math.max(planNeeded, dailyNeeded);
    const surplus      = purchased - totalNeeded;
    const pctUsed      = totalNeeded > 0 ? Math.min((purchased / totalNeeded) * 100, 100) : 100;

    // Avg weekly use — use whichever source drives totalNeeded
    const weeklyFromPlan  = assignedWeekCount > 0 ? planNeeded / assignedWeekCount : 0;
    const weeklyFromDaily = dailyUse * 7;
    const avgWeeklyTotal  = Math.max(weeklyFromPlan, weeklyFromDaily);
    const weeksLeft       = avgWeeklyTotal > 0 ? purchased / avgWeeklyTotal : null;

    return {
      ...g,
      planNeeded:    Math.round(planNeeded),
      dailyNeeded:   Math.round(dailyNeeded),
      monthlyNeeded: Math.round(totalNeeded),
      weeklyUse:     Math.round(weeklyFromPlan),
      purchased,
      surplus:       Math.round(surplus),
      pctUsed:       Math.round(pctUsed),
      weeksLeft:     weeksLeft ? Math.round(weeksLeft * 10) / 10 : null,
      assignedWeeks: itemWeekCount || assignedWeekCount,
      hasFallback,
      status: totalNeeded === 0 && dailyUse === 0 ? "unused"
            : surplus < 0                         ? "shortage"
            : surplus < totalNeeded * 0.1         ? "tight"
            : "good",
      nutrients: lookupNutrients(g.name),
    };
  });
}
